
















import { defineComponent, ref } from "@vue/composition-api";
import { PayeeInformation } from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import {
  InputPayoutRequestSearch,
  PayoutRequest,
  PayoutRequestSettings,
} from "@scrile/api-provider/dist/api/PayoutRequestsProvider";
import { PayoutSystemSettings } from "@scrile/api-provider/dist/api/PayoutSystemSettingsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import { handleRequestError } from "@/lib/handleRequestError";
import { ERRORS } from "@/const";
import PayoutRequestsView from "./components/PayoutRequestsView.vue";
import usePendingPrimaryBalance from "./hooks/usePendingPrimaryBalance";

export default defineComponent({
  name: "PagePayoutRequests",
  components: { PayoutRequestsView },
  setup() {
    const { getBalance, pendingBalance, primaryBalance } = usePendingPrimaryBalance();
    const { authUserStrict } = useAuthUser();
    const loading = ref(false);
    const requestErrorValue = ref("");

    const requestSettings = ref<PayoutRequestSettings | null>(null);
    const payeeInfo = ref<PayeeInformation | null>(null);
    const payoutSystemSettings = ref<PayoutSystemSettings[]>([]);

    const {
      enableScroll,
      items: payoutRequests,
      getFirstPage,
      getNextPage,
      addItem,
    } = usePager<PayoutRequest, InputPayoutRequestSearch>(page => {
      return providers.PayoutRequestsProvider.search({
        data: { userId: authUserStrict.value.id, sort: "TIME,DESC", page },
      });
    });

    const onCreateRequest = async ({
      value,
      cb,
      loadingCb,
    }: {
      value: string;
      cb: () => void;
      loadingCb: () => void;
    }) => {
      if (isNaN(+value)) return;
      try {
        providers.PayoutRequestsProvider.setReceiveErrors(false);
        const response = await providers.PayoutRequestsProvider.create({
          data: {
            amount: +value,
            userId: authUserStrict.value.id,
          },
        });
        addItem(response);
        cb();
      } catch (e) {
        handleRequestError(e, [
          {
            comparator: e =>
              e.details?.messageTemplate === ERRORS.PAYOUT_FIELD_AMOUNT_ERROR ||
              e.details?.messageTemplate === ERRORS.PAYOUT_NOT_ENOUGH_FUNDS_ERROR,
            onSuccessCB: e => (requestErrorValue.value = e.message ?? "Error"),
          },
        ]);
      } finally {
        loadingCb();
      }
    };

    const getRequestSettings = async () => {
      const response = await providers.PayoutRequestsProvider.getSettings({
        data: { userIds: [authUserStrict.value.id] },
      });
      requestSettings.value = response.find(i => i.userId === authUserStrict.value.id) ?? null;
    };

    const getPayeeInfo = async () => {
      payeeInfo.value = await providers.PayeeInformationProvider.find({ id: authUserStrict.value.id });
    };

    const getPayoutSystemSettings = async () => {
      payoutSystemSettings.value = await providers.PayoutSystemSettingsProvider.findAll();
    };

    const getData = async () => {
      try {
        loading.value = true;
        await Promise.all([
          getFirstPage(),
          getBalance(),
          getRequestSettings(),
          getPayeeInfo(),
          getPayoutSystemSettings(),
        ]);
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      loading,
      enableScroll,
      primaryBalance,
      pendingBalance,
      payoutRequests,
      payeeInfo,
      payoutSystemSettings,
      requestSettings,
      requestErrorValue,
      getNextPage,
      addItem,
      onCreateRequest,
    };
  },
});
